import React, { FC } from "react";
import {
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";

import Modal from "./base/Modal";

import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { ILedger, LedgerHead } from "../../interfaces/ledger";
import StyledButton from "../StyledButton";
import StyledIcon from "../StyledIcon";
import Divider from "../common/Divider";
import { ledgerService } from "../../services/LedgerService";
import { AuthContext } from "../../contexts/auth/Store";
import { formatCurrency } from "../../utils/money";
import { useNavigation } from "@react-navigation/native";
import { AnalyticsService } from "../../constants/Analytics";
import { IBuyer } from "../../interfaces/buyer";
import { PaystackButton, usePaystackPayment } from "react-paystack";
import config from "../../constants/Config";

interface IProps {
  modalState: boolean;
  hideModal: () => void;
  ledger: ILedger;
}

const analytics = new AnalyticsService();

const BalanceAccountModal: FC<IProps> = ({ modalState, hideModal, ledger }) => {
  const navigation = useNavigation();
  const auth = React.useContext(AuthContext);
  const buyer: IBuyer = auth.state.buyerData;
  const clickAreaRef = React.useRef<any>();

  const handleClickOutside = (e: any) => {
    e.persist();
    if (clickAreaRef.current) {
      if (e.target === clickAreaRef.current?._nativeTag) {
        hideModal();
      }
    }
    return true;
  };

  const balance = React.useMemo(() => {
    return Math.abs(ledger?.balance || 0);
  }, [ledger]);

  const [loadingUrl, setLoadingUrl] = React.useState<boolean>(false);
  const payNow = () => {
    setLoadingUrl(true);
    const callback_url =
      (Platform.OS === "web" ? window.location.host : "https://suplias.shop") +
      "/balance";

    ledgerService
      .initiatePaystackCheckout({
        parent_id: buyer._id,
        parent_type: LedgerHead.Buyer,
        amount: balance,
        callback_url,
      })
      .then((data) => {
        hideModal();
        setLoadingUrl(false);
        if (data?.authorization_url) {
          if (Platform.OS === "web") {
            setTimeout(
              () => (window.location = data?.authorization_url),
              10000
            );
          } else {
            navigation.navigate("PaystackCheckout", { ...data });
          }
        } else {
          analytics.logErrorMessage(
            data.message || "Paystack checkout url could not be generated."
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setLoadingUrl(false);
      });
  };

  const paymentProps = React.useMemo(() => {
    return {
      email: `${buyer._id}.buyer@s3.suplias.com`,
      amount: Math.ceil(balance * 100),
      label: buyer.name,
      metadata: {
        name: buyer.name,
        phone: buyer.phone,
        user_id: buyer.user_id,
      },
      channels: ["bank_transfer"],
      publicKey: config.paystack_key,
      text: "Pay Now",
    };
  }, [balance, buyer, navigation]);

  const paystackInstance = usePaystackPayment(paymentProps);

  return (
    <Modal
      presentationStyle="overFullScreen"
      animationType="slide"
      transparent={true}
      visible={modalState}
      onRequestClose={() => {
        hideModal();
      }}
    >
      <View
        ref={clickAreaRef}
        onStartShouldSetResponder={handleClickOutside}
        style={styles.overlay}
      >
        <View style={styles.container}>
          <View style={[styles.card]}>
            <View style={styles.headerContainer}>
              <Text style={styles.headerText}>Pay with Transfer</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => {
                  hideModal();
                }}
              >
                <StyledIcon
                  iconName="close"
                  iconColor={Colors.greyColor}
                  size={18}
                  iconOnly
                />
              </TouchableOpacity>
            </View>

            <View style={[styles.detailContainer, styles.balanceContainer]}>
              <Text style={styles.label}>PAY</Text>
              <Text style={styles.macroTitle}>{formatCurrency(balance)}</Text>
            </View>

            <View style={styles.detailContainer}>
              <Divider margin={20} />
            </View>

            <View style={styles.detailContainer}>
              {Platform.OS === "android" ? (
                <StyledButton
                  label="Pay Now"
                  submitting={loadingUrl}
                  disabled={loadingUrl}
                  action={payNow}
                />
              ) : (
                <StyledButton
                  label="Pay Now"
                  action={() =>
                    paystackInstance(
                      () => {
                        hideModal();
                        navigation.navigate("Balance");
                      },
                      () => null
                    )
                  }
                />
              )}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default BalanceAccountModal;

const styles = StyleSheet.create({
  overlay: {
    backgroundColor: "rgba(0,0,0,0.6)",
    height: "100%",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    paddingHorizontal: 16,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    alignContent: "center",
  },
  headerText: {
    ...FontStyles.size14,
    ...FontStyles.lh22,
    ...FontStyles.semibold,
    flexGrow: 1,
    paddingLeft: 16,
  },
  closeButton: {
    flexDirection: "row",
    alignSelf: "flex-end",
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 16,
    marginHorizontal: 20,
    paddingBottom: 20,
  },
  detailContainer: {
    paddingHorizontal: 20,
  },
  balanceContainer: {
    alignItems: "center",
  },

  label: {
    ...FontStyles.size12,
    ...FontStyles.lh16,
    ...FontStyles.gray,
    ...FontStyles.medium,
  },
  macroTitle: {
    ...FontStyles.size18,
    ...FontStyles.lh22,
    ...FontStyles.bold,
    color: Colors.dangerText,
  },
});
